/**
 * @fileoverview Centralized authentication utilities and form validations
 * @active Used throughout the application for auth operations
 *
 * This file provides centralized utilities for authentication operations and validation,
 * ensuring consistent behavior and error handling across the application.
 * All auth-related validation schemas are consolidated here to avoid duplication.
 *
 * @see {@link src/services/auth.service.ts} - Main auth service
 * @see {@link src/types/userAuth.ts} - Auth type definitions
 * @see {@link src/app/utils/supabase/client.ts} - Supabase client
 * @see {@link src/app/utils/supabase/server.ts} - Server-side Supabase client
 * @see {@link src/components/auth/SignIn.tsx} - Sign in form
 * @see {@link src/components/auth/SignUp.tsx} - Sign up form
 */

/**
 * Account locking prevention - Moved from supabase/client.ts
 */
export const accountLocking = {
  /**
   * Lock an account after too many failed attempts
   * @param email - User email
   */
  lock: (email: string): void => {
    if (typeof window === "undefined") return;
    const lockData = localStorage.getItem("locked_accounts") || "{}";
    try {
      const locked = JSON.parse(lockData);
      locked[email] = Date.now() + 30 * 60 * 1000; // Lock for 30 minutes
      localStorage.setItem("locked_accounts", JSON.stringify(locked));
    } catch (e) {
      console.error("Error locking account:", e);
    }
  },

  /**
   * Check if an account is locked
   * @param email - User email
   * @returns True if account is locked
   */
  isLocked: (email: string): boolean => {
    if (typeof window === "undefined") return false;
    try {
      const lockData = localStorage.getItem("locked_accounts") || "{}";
      const locked = JSON.parse(lockData);
      const lockUntil = locked[email] || 0;
      return Date.now() < lockUntil;
    } catch (e) {
      console.error("Error checking if account is locked:", e);
      return false;
    }
  },

  /**
   * Determine if the account should be locked based on attempts and current lock status
   * @param attempts - Number of failed attempts
   * @param isLocked - Whether the account is currently locked
   * @returns True if the account should be locked
   */
  shouldLockAccount: (attempts: number, isLocked: boolean): boolean => {
    // Lock after 5 failed attempts
    return attempts >= 5 && !isLocked;
  },

  /**
   * Duration for account lockout in milliseconds (30 minutes)
   */
  lockoutDuration: 30 * 60 * 1000,
};

import {
  type User,
  type AuthError,
  type Provider,
} from "@supabase/supabase-js";
import { createClientComponent } from "@/app/utils/supabase/client";
import {
  type CUSTOM_ERRORObject,
  type AuthSuccessResponse,
  type AuthProvider,
  type SignInParams,
  type SignUpParams,
  type PasswordConfirmationData,
  getAuthErrorMessage,
} from "@/types/userAuth";
import { authActions } from "@/store/auth/auth.slice";
import { loadingError } from "@/services/error.service";
import { UseToastOptions } from "@chakra-ui/react";
import * as yup from "yup";

/**
 * Regular expressions for validation
 */
export const PATTERNS = {
  /** Validates names to only contain letters, spaces, hyphens, and apostrophes */
  NAME: /^[a-zA-Z\s\-']+$/,
  /** Validates URLs */
  URL: new RegExp(
    `(http(s)?://)?(www.)?[a-zA-Z0-9@:%._+~#=]{2,256}.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_+.~#?&/=]*)`
  ),
  /** Validates phone numbers */
  PHONE: /^[0-9+\-\s]*$/,
  /** Validates password special characters */
  PASSWORD_SPECIAL: /[!@#$%^&*()+\-=.{}[\]|\\:;"'<>,.?/~`]/,
} as const;

/**
 * Cookie utilities - Consolidated from utils/auth.ts
 */

/**
 * Set a cookie with the given name and value
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 */
export const setCookie = (name: string, value: string): void => {
  // Set cookie with path and far-future expiration
  document.cookie = `${name}=${value}; path=/; expires=Tue, 19 Jan 2038 03:14:07 GMT`;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name to retrieve
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

/**
 * Remove a cookie by name
 * @param {string} name - Cookie name to remove
 */
export const removeCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

/**
 * Determines if registration is required based on environment setting
 * Used for conditionally showing login/registration prompts
 *
 * @returns {boolean} True if registration is required
 */
export const isRegistrationRequired = (): boolean => {
  return process.env.NEXT_PUBLIC_REQUIRE_REGISTRATION === "true";
};

/**
 * Generic handler for auth operations with consistent error handling
 * @param operation - Async operation to perform
 * @param dispatch - Redux dispatch function
 */
export async function handleAuthOperation<T>(
  operation: () => Promise<T>,
  dispatch: Function
): Promise<T> {
  try {
    return await operation();
  } catch (error: unknown) {
    dispatch(loadingError(error));
    throw error;
  }
}

/**
 * Get current authenticated user
 * @returns The current authenticated user or null
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const supabase = createClientComponent();
    const { data } = await supabase.auth.getUser();
    return data.user;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
}

/**
 * Check if user is authenticated client-side
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser();
  return !!user;
}

/**
 * Password validation rules for auth forms
 * Includes checks for length, complexity, and special characters
 * @returns Yup validation schema for passwords
 */
export const passwordValidationRules = () => {
  return yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must include at least one lowercase letter")
    .matches(/[A-Z]/, "Password must include at least one uppercase letter")
    .matches(/[0-9]/, "Password must include at least one number")
    .matches(
      PATTERNS.PASSWORD_SPECIAL,
      "Password must include at least one special character"
    );
};

/**
 * Auth form validation utilities and schemas
 * Centralizes all auth-related validation logic
 */
export const authValidation = {
  /**
   * Verify all required form fields are filled
   * @param data Form data object to validate
   * @param requiredFields Array of required field names
   * @returns True if all required fields are filled
   */
  verifyFormData: (
    data: Record<string, any>,
    requiredFields?: string[]
  ): boolean => {
    if (!requiredFields) return true;
    return requiredFields.every((field) => !!data[field]);
  },

  /**
   * Get required fields for different auth forms
   */
  requiredFields: {
    signIn: ["email", "password"],
    signUp: ["firstName", "lastName", "email", "password", "confirmPassword"],
    magicLink: ["email"],
    forgotPassword: ["email"],
  },

  /**
   * Validate auth operation data before submission
   * @param data Form data to validate
   * @param operation Auth operation type
   * @returns True if data is valid for the operation
   */
  validateAuthData: (
    data: Record<string, any>,
    operation: keyof typeof authValidation.requiredFields
  ): boolean => {
    const fields = authValidation.requiredFields[operation];
    return authValidation.verifyFormData(data, fields);
  },
};

/**
 * Common toast configurations for auth operations
 * Use these directly in components with RTK Query hooks
 */
export const authToast = {
  ERROR: {
    title: "Error",
    status: "error",
    duration: 5000,
    isClosable: true,
  } as UseToastOptions,

  SUCCESS: {
    title: "Success",
    status: "success",
    duration: 3000,
    isClosable: true,
  } as UseToastOptions,

  /**
   * Create a success toast config with custom message
   * @param {string} message - Success message to display
   * @returns {UseToastOptions} Toast configuration
   */
  createSuccessToast: (message: string): UseToastOptions => ({
    title: "Success",
    description: message,
    status: "success",
    duration: 3000,
    isClosable: true,
  }),

  /**
   * Create an error toast config with user-friendly message
   * Uses centralized error message mapping from userAuth.ts
   *
   * @param {unknown} error - Error object from API response
   * @returns {UseToastOptions} Toast configuration
   */
  createErrorToast: (error: unknown): UseToastOptions => ({
    title: "Error",
    description:
      typeof error === "object" && error !== null
        ? getAuthErrorMessage({
            message:
              error instanceof Error
                ? error.message
                : "data" in (error as any)
                  ? String((error as any).data)
                  : "message" in (error as any)
                    ? String((error as any).message)
                    : "Unknown error",
            status:
              "status" in (error as any) ? Number((error as any).status) : 400,
          })
        : String(error),
    status: "error",
    duration: 5000,
    isClosable: true,
  }),
};

/**
 * Sign in form validation schema with email and password validation
 * @see {@link src/components/auth/SignIn.tsx} - Sign in form
 */
export const signInSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  password: yup.string().required("Password is required"),
  onClose: yup.mixed().optional(), // Optional callback
});

/**
 * Sign up form validation schema with comprehensive validation
 * @see {@link src/components/auth/SignUp.tsx} - Sign-up form
 */
export const signUpSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .required("First name is required")
    .matches(
      PATTERNS.NAME,
      "The name can only contain letters, spaces, hyphens, and apostrophes"
    ),
  lastName: yup
    .string()
    .trim()
    .required("Last name is required")
    .matches(
      PATTERNS.NAME,
      "The surname can only contain letters, spaces, hyphens, and apostrophes"
    ),
  email: yup
    .string()
    .trim()
    .email("Invalid email format")
    .required("Email is required"),
  password: passwordValidationRules(),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password")], "Passwords must match")
    .required("Confirm Password is required"),
  metadata: yup
    .object()
    .shape({
      first_name: yup.string(),
      last_name: yup.string(),
    })
    .optional(),
  onClose: yup.mixed().optional(),
});

/**
 * Magic link form validation schema with email validation
 * @see {@link src/components/auth/MagicSignIn/MagicSignIn.tsx} - Magic link form
 */
export const magicLinkSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
});

/**
 * Forgot password form validation schema with email validation
 * @see {@link src/components/auth/forgotPassword/index.tsx} - Forgot password form
 */
export const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
});
